<template>
  <div>
    <!-- Trigger Button -->
    <v-btn 
      :disabled="disabled" 
      color="primary" 
      :loading="loading" 
      @click="fetch" 
      text
      class="font-weight-black text-uppercase tracking-widest rounded-lg px-4 transition-fast-in-fast-out h-44"
    >
      <v-icon left size="16">fas fa-file-export</v-icon>
      Exportar
    </v-btn>

    <!-- Download Dialog -->
    <v-dialog v-model="dataReady" max-width="440" transition="scale-transition">
      <v-card class="modern-card overflow-hidden rounded-xl">
        <!-- Premium Header -->
        <div class="bg-grey-light pa-6 border-bottom">
          <div class="d-flex align-center">
            <v-avatar color="primary" size="40" class="mr-4 shadow-sm border-white-sm">
              <v-icon color="white" small>fas fa-download</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-black primary--text lh-1">Exportar Post-Costo</div>
              <div class="caption grey--text font-weight-bold text-uppercase tracking-wider mt-1">Análisis de Rentabilidad</div>
            </div>
          </div>
        </div>

        <v-card-text class="text-center pa-8">
          <v-avatar color="primary lighten-5" size="80" class="mb-6">
            <v-icon color="primary" size="40">fas fa-calculator</v-icon>
          </v-avatar>
          <div class="body-1 primary--text font-weight-bold mb-2">¡Reporte de Costos Listo!</div>
          <div class="caption grey--text px-4 mb-8">Seleccione si desea exportar el listado general de OTs o el detalle por producto.</div>

          <div class="d-flex flex-column gap-3">
            <vue-json-to-csv
              :json-data="workOrders"
              :labels="labels"
              :csv-title="csvTitle"
              :separator="';'"
              @success="val => handleSuccess(val)"
              @error="val => handleError(val)">
              <v-btn 
                color="success" 
                block 
                large 
                elevation="0"
                height="48"
                class="font-weight-black text-none rounded-lg shadow-sm"
              >
                <v-icon left>fas fa-list-ol</v-icon>
                Descargar Lista Resumen
              </v-btn>
            </vue-json-to-csv>

            <vue-json-to-csv
              :json-data="products"
              :labels="detailLabels"
              :csv-title="'detalle_'+csvTitle"
              :separator="';'"
              @success="val => handleSuccess(val)"
              @error="val => handleError(val)">
              <v-btn 
                color="primary" 
                block 
                large 
                outlined
                height="48"
                class="font-weight-black text-none rounded-lg"
              >
                <v-icon left>fas fa-boxes</v-icon>
                Descargar Detalle Productos
              </v-btn>
            </vue-json-to-csv>
          </div>
        </v-card-text>

        <v-divider class="mx-8 opacity-10"></v-divider>
        <div class="pa-4 text-center">
          <v-btn text small color="grey" class="text-none font-weight-bold" @click="dataReady = false">Cerrar</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import {fetchPostCostWorkOrders} from '@/api/reports'
import VueJsonToCsv from 'vue-json-to-csv'
import moment from 'moment'

export default {
  name: "ExportPostCost",
  props: ['filter', 'options', 'disabled', 'search', 'dateMode'],
  components: { VueJsonToCsv },
  data() {
    return {
      loading: false,
      workOrders: [],
      products: [],
      dataReady: false,
      labels: { 
        id: { title: 'Nro' },
        created_at: { title: 'Creada' },
        completed_at: { title: 'Finalizada' },
        name: { title: 'Proyecto' },
        sales_note: { title: 'Cotización' },
        customer_name: { title: 'Cliente' },
        customer_tipo: { title: 'Tipo Cliente' },
        customer_industry: { title: 'Industria' },
        customer_rubro: { title: 'Rubro' },
        seller_full_name: { title: 'Vendedor' },
        seller_bussiness_unit: { title: 'Unidad de Negocio' },
        sales_total: { title: 'Venta' },
        sales_cost: { title: 'Costo Teórico' },
        estimated_margin: { title: 'Margen Teórico' },
        total: { title: 'Costo Real' },
        real_margin: { title: 'Margen Real' },
        production_cost: { title: 'Costo de Producción' },
        reprocess_cost: { title: 'Costo de Reproceso' },
        consume_cost: { title: 'Costo de Consumo Producción' },
        consume_cost_reprocess: { title: 'Costo de Consumo Reproceso' },
        additional_costs: { title: 'Costo Adicional' },
        purchase_orders_costs: { title: 'Costo de Ordenes de Compra' },
        warehouse_consumption: { title: 'Costo de Consumo de Bodega' },
      },
      detailLabels: {
        id: { title: 'OT' },
        created_at: { title: 'Creada' },
        completed_at: { title: 'Finalizada' },
        name: { title: 'Proyecto' },
        sales_note: { title: 'Cotización' },
        customer_name: { title: 'Cliente' },
        seller_full_name: { title: 'Vendedor' },
        sku: { title: 'SKU' },
        product_name: { title: 'Producto' },
        line: { title: 'Linea' },
        family: { title: 'Familia' },
        qty: { title: 'Cantidad' },
        mts2: { title: 'Mts2' },
        total_price: { title: 'Precio Total' },
        teoric_cost: { title: 'Costo Teórico' },
        real_cost: { title: 'Costo Real' },
      },
      csvTitle: `export_postcost_report_${moment(new Date().toISOString()).format('DD-MM-yyyy_HH:mm')}`
    }
  },
  methods: {
    async fetch() {
      this.loading = true
      var created_before = ''
      var created_after = ''
      if (this.filter.created_after) created_after = moment(this.filter.created_after).utc().format('yyyy-MM-DD HH:mm')
      if (this.filter.created_before) created_before = moment(this.filter.created_before).utc().add(1439, 'minutes').format('yyyy-MM-DD HH:mm')
      
      const customer = this.filter.customer?.id || ''
      const { sortBy, sortDesc } = this.options
      const sort = (sortBy && sortBy.length) ? sortBy[0] : 'id'
      const dateParams = this.dateMode === 'created'
        ? `created_after=${created_after}&created_before=${created_before}`
        : `completed_at_after=${created_after}&completed_at_before=${created_before}`
      const query = `ordering=${sortDesc && sortDesc[0] ? sort : '-' + sort}&search=${this.search || ''}&page=1&page_size=1000&${dateParams}&sales_note__customer=${customer}&completed=true`
      
      this.workOrders = []
      this.products = []
      
      await fetchPostCostWorkOrders(query)
        .then((data) => {
          this.workOrders = data.results.map(item => {
            const { additional_costs, warehouse_consumption, consume_cost, consume_cost_reprocess, production_cost, reprocess_cost, purchase_orders_costs, sales_cost, sales_total, total, products } = item.real_price || {}
            
            item.created_at = moment(item.created_at).format('DD-MM-yyyy HH:mm')
            item.completed_at = moment(item.completed_at).format('DD-MM-yyyy HH:mm')
            item.estimated_margin = sales_total != 0 ? (1 - sales_cost / sales_total) * 100 : 0
            item.real_margin = total != 0 ? (1 - sales_cost / total) * 100 : 0
            
            if (products) {
              products.forEach(product => { 
                this.products.push({ ...product, ...item })
              })
            }
          
            return { ...item, additional_costs, consume_cost, consume_cost_reprocess, production_cost, reprocess_cost, purchase_orders_costs, sales_cost, sales_total, total, warehouse_consumption }
          })
          this.dataReady = true
        })
        .catch((err) => notifications.showError('Error al cargar Post-Costo: ' + err))
        .finally(() => this.loading = false)
    },
    handleSuccess(val) {
      notifications.showSuccess('Reporte Post-Costo Exportado')
      this.dataReady = false
    },
    handleError(val) {
      notifications.showError('Error Exportando Post-Costo')
      this.dataReady = false
    }
  }
}
</script>

<style scoped>
.modern-card { border-radius: 12px !important; font-family: 'Outfit', sans-serif !important; }
.bg-grey-light { background-color: #f8fafc !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.06) !important; }
.border-white-sm { border: 2px solid white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important; }
.h-44 { height: 44px !important; }
.lh-1 { line-height: 1 !important; }
.tracking-widest { letter-spacing: 1.5px; }
.tracking-wider { letter-spacing: 0.8px; }
.gap-3 { gap: 12px; }
</style>